import React from "react";

function fmt(n, digits = 2) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString("en-IN", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function valueTone(key, summary, raw) {
  if (key === "level") return levelClass(summary);
  if (key === "pnl" || key === "equity" || key === "free" || key === "withdrawable") {
    const n = Number(raw);
    if (!Number.isFinite(n) || n === 0) return "";
    return n < 0 ? "account-margin-bar__cell--neg" : "account-margin-bar__cell--pos";
  }
  return "";
}

function levelClass(summary) {
  if (!summary || summary.marginLevel == null || summary.usedMargin <= 0) return "";
  const level = Number(summary.marginLevel);
  const call = Number(summary.marginCallPercent ?? 50);
  const liq = Number(summary.liquidationPointPercent ?? 10);
  if (level <= liq) return "account-margin-bar__level--danger";
  if (level <= call) return "account-margin-bar__level--warn";
  return "account-margin-bar__level--ok";
}

/**
 * Margin Module §11.1 — client account bar.
 * @param {"default"|"compact"|"footer"} variant
 */
export default function AccountMarginBar({
  summary,
  loading = false,
  compact = false,
  variant,
  onRefresh,
  /** Wallet page only — hide on front-page footer / side menu. */
  showWithdrawable = false,
}) {
  const mode = variant || (compact ? "compact" : "default");

  if (!summary && !loading) {
    return (
      <div className={`account-margin-bar account-margin-bar--${mode}`}>
        <div className="account-margin-bar__empty">Margin metrics unavailable</div>
      </div>
    );
  }

  const level =
    summary?.marginLevel == null || summary?.usedMargin <= 0
      ? "—"
      : `${fmt(summary.marginLevel, 1)}%`;

  const cells = [
    { key: "balance", label: "Balance", value: fmt(summary?.balance), raw: summary?.balance },
    { key: "credit", label: "Credit", value: fmt(summary?.credit), raw: summary?.credit },
    { key: "equity", label: "Equity", value: fmt(summary?.equity), raw: summary?.equity },
    { key: "pnl", label: "P&L", value: fmt(summary?.pnl), raw: summary?.pnl },
    { key: "used", label: "Used", value: fmt(summary?.usedMargin), raw: summary?.usedMargin },
    { key: "free", label: "Free", value: fmt(summary?.freeMargin), raw: summary?.freeMargin },
    ...(showWithdrawable
      ? [
          {
            key: "withdrawable",
            label: "Withdrawable",
            value: fmt(summary?.withdrawable),
            raw: summary?.withdrawable,
          },
        ]
      : []),
    { key: "holding", label: "Holding", value: fmt(summary?.holdingMargin), raw: summary?.holdingMargin },
    { key: "level", label: "Level", value: level, raw: summary?.marginLevel },
  ];

  if (summary?.blockedEquity > 0) {
    cells.push({
      key: "blocked",
      label: "Blocked",
      value: fmt(summary.blockedEquity),
      raw: summary.blockedEquity,
    });
  }

  return (
    <div
      className={`account-margin-bar account-margin-bar--${mode}${
        loading ? " account-margin-bar--loading" : ""
      }`}
      role="status"
      aria-live="polite"
    >
      <div className="account-margin-bar__scroll">
        <div className="account-margin-bar__row">
          {cells.map((c) => (
            <div
              key={c.key}
              className={`account-margin-bar__cell ${valueTone(c.key, summary, c.raw)} ${
                c.key === "level" ? levelClass(summary) : ""
              }`}
            >
              <span className="account-margin-bar__label">{c.label}</span>
              <span className="account-margin-bar__value">
                {loading && !summary ? "···" : c.value}
              </span>
            </div>
          ))}
        </div>
      </div>
      {onRefresh && (
        <button
          type="button"
          className="account-margin-bar__refresh"
          disabled={loading}
          onClick={onRefresh}
          title="Refresh margin"
          aria-label="Refresh margin"
        >
          ↻
        </button>
      )}
    </div>
  );
}

/** Primary pill value: Equity (falls back to Free, then Balance). */
export function formatEquityPill(summary) {
  if (!summary) return null;
  const n = summary.equity ?? summary.freeMargin ?? summary.balance;
  if (n == null) return null;
  return fmt(n);
}

export function equityPillColor(summary) {
  if (!summary) return "var(--green)";
  if (Number(summary.equity) < 0 || Number(summary.freeMargin) < 0) return "var(--red)";
  const level = summary.marginLevel;
  if (level != null && summary.usedMargin > 0) {
    if (level <= Number(summary.liquidationPointPercent ?? 10)) return "var(--red)";
    if (level <= Number(summary.marginCallPercent ?? 50)) return "#e6a700";
  }
  return "var(--green)";
}
